var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { filter, values, isEqual } from 'lodash';
import resources from 'app/store/resources';
import { getResources } from 'app/store/selectors/getResources';
import { fetchDashboardData, fetchOneDashboard } from 'app/store/reducers/dashboards';
import { selectedDashboardSelector } from 'app/store/selectors/dashboards';
import { widgetsByDashboardSelector } from 'app/store/selectors/widgets';
import Loader from 'app/components/Loader/Loader.jsx';
import { WIDGET_TYPES } from 'app/pages/Project/ProductionManagement/Dashboards/constants';
import DashboardWidgets from 'app/pages/Project/ProductionManagement/Dashboards/DashboardWidgets/DashboardWidgets';
import DashboardHeader from 'app/pages/Project/ProductionManagement/Dashboards/DashboardHeader/DashboardHeader';
import { permission } from 'app/containers/Permissions';
import classes from 'app/pages/Project/ProductionManagement/Dashboards/styles.module.scss';
import { confirmDelete } from 'app/components/Modal';
const Dashboard = () => {
    const { projectId, dashboardId } = useParams();
    const dispatch = useDispatch();
    const isSupervisor = permission(['projet_global__supervisor']);
    const isWidgetSupervisor = (widget, allWidgets) => {
        const widgetHasParent = Boolean(widget.parent);
        if (!widgetHasParent && !widget.rel_dashboardWidgetInst.isSupervisor) {
            return false;
        }
        if (widgetHasParent) {
            const widgetParent = allWidgets.find((wid) => wid.id === widget.parent);
            if (widgetParent) {
                const isSupervisor = widget.rel_dashboardWidgetInst.isSupervisor || isWidgetSupervisor(widgetParent, allWidgets);
                return isSupervisor;
            }
        }
        return true;
    };
    const [loading, setLoading] = useState(false);
    const [viewMode, setViewMode] = useState(isSupervisor ? 'sup' : 'public');
    const [dashboardLayout, setDashboardLayout] = useState([]);
    const [activeEpisodesByWidget, setActiveEpisodesByWidget] = useState([]);
    const progressionStatus = getResources(undefined, 'progressionStatus');
    const stepProjects = getResources(undefined, 'steps');
    const allEpisodes = useSelector((state) => filter(state.assets.resources, (asset) => asset.assetType === 'ep'));
    const activeDashboard = useSelector(selectedDashboardSelector(dashboardId));
    const dashboardWidgets = useSelector(widgetsByDashboardSelector(dashboardId));
    const widgets = isSupervisor && viewMode === 'sup'
        ? dashboardWidgets
        : dashboardWidgets.filter((widget) => !isWidgetSupervisor(widget, dashboardWidgets));
    const onRestoreDefaultLayout = () => {
        confirmDelete({
            title: 'Restore default layout',
            render: 'Are you sure you want to restore the default layout ?',
            validateLabel: 'Yes',
            onValidate: () => {
                const localStorageDashboardsData = localStorage.getItem('dashboardLayouts');
                if (localStorageDashboardsData) {
                    const dashboardsLs = JSON.parse(localStorageDashboardsData);
                    delete dashboardsLs[dashboardId][viewMode];
                    localStorage.setItem('dashboardLayouts', JSON.stringify(dashboardsLs));
                    const layout = generateLayouts(widgets, true);
                    setDashboardLayout(layout);
                }
            },
            validateMessage: 'Local layout removed',
        });
    };
    const saveDashboardLayoutsInLS = (updatedLayout) => {
        let dashboardsLs = {};
        const localStorageDashboardsData = localStorage.getItem('dashboardLayouts');
        if (localStorageDashboardsData) {
            dashboardsLs = JSON.parse(localStorageDashboardsData);
        }
        const updatedLsDashboards = Object.assign(Object.assign({}, dashboardsLs), { [dashboardId]: Object.assign(Object.assign({}, dashboardsLs[dashboardId]), { [viewMode]: updatedLayout }) });
        localStorage.setItem('dashboardLayouts', JSON.stringify(updatedLsDashboards));
    };
    const saveDashboardLayoutLocally = (newLayouts) => {
        setDashboardLayout((prevState) => {
            const updatedLayout = prevState.map((layout) => {
                const el = newLayouts.find((nl) => nl.i === layout.i);
                if (el) {
                    return Object.assign(Object.assign({}, layout), { h: el.h, w: el.w, x: el.x, y: el.y });
                }
                return layout;
            });
            if (!isEqual(prevState, updatedLayout)) {
                saveDashboardLayoutsInLS(updatedLayout);
            }
            return updatedLayout;
        });
    };
    const generateDefaultWidgetLayout = (wid) => {
        if (wid.widgetType === WIDGET_TYPES.CONTAINER) {
            return {
                w: 6,
                h: 22,
                x: 0,
                y: 0,
                i: wid.id,
                name: wid.name,
                moved: false,
                static: false,
            };
        }
        return {
            w: 6,
            h: 10,
            x: 0,
            y: 0,
            i: wid.id,
            name: wid.name,
            moved: false,
            static: false,
        };
    };
    const generateLayouts = (widgets, skipLSCheck) => {
        var _a, _b, _c;
        const localStorageDashboardsData = localStorage.getItem('dashboardLayouts');
        const savedLayout = (_a = activeDashboard.config.layouts) === null || _a === void 0 ? void 0 : _a[viewMode];
        // generate layout from ls if it exists
        if (localStorageDashboardsData && ((_b = JSON.parse(localStorageDashboardsData)[dashboardId]) === null || _b === void 0 ? void 0 : _b[viewMode]) && !skipLSCheck) {
            const dashboardsLs = ((_c = JSON.parse(localStorageDashboardsData)) === null || _c === void 0 ? void 0 : _c[dashboardId]) || [];
            const layouts = widgets.map((widget) => {
                var _a;
                const lsWidgetLayout = (_a = dashboardsLs[viewMode]) === null || _a === void 0 ? void 0 : _a.find((lay) => lay.i === widget.id);
                if (lsWidgetLayout) {
                    return lsWidgetLayout;
                }
                else {
                    return generateDefaultWidgetLayout(widget);
                }
            });
            return layouts;
        }
        // generate layout from saved layout in dashboard config if it exists
        if (savedLayout) {
            const layouts = widgets.map((widget) => {
                const savedWidgetLayout = savedLayout === null || savedLayout === void 0 ? void 0 : savedLayout.find((lay) => lay.i === widget.id);
                if (savedWidgetLayout) {
                    return savedWidgetLayout;
                }
                else {
                    return generateDefaultWidgetLayout(widget);
                }
            });
            return layouts;
        }
        // else generate default layout
        const layouts = widgets.map((widget) => {
            return generateDefaultWidgetLayout(widget);
        });
        return layouts;
    };
    const checkLocalLayout = () => {
        var _a;
        const localStorageDashboardsData = localStorage.getItem('dashboardLayouts');
        if (localStorageDashboardsData) {
            const dashboardsLs = JSON.parse(localStorageDashboardsData);
            if ((_a = dashboardsLs[dashboardId]) === null || _a === void 0 ? void 0 : _a[viewMode]) {
                return true;
            }
        }
        return false;
    };
    useEffect(() => {
        ;
        (() => __awaiter(void 0, void 0, void 0, function* () {
            setLoading(true);
            const promises = [
                permission(['projet_dashboards__read']) && dispatch(fetchOneDashboard(dashboardId)),
                dispatch(fetchDashboardData(activeDashboard.id)),
                !allEpisodes.length &&
                    resources.assets.fetchEpisodes(projectId, {
                        params: {
                            queries: { page_size: 1000 },
                        },
                    }),
                !values(stepProjects).length && resources.steps.fetchByProject(projectId),
            ];
            yield Promise.all(promises);
            setLoading(false);
        }))();
    }, [dashboardId]);
    useEffect(() => {
        if (!loading) {
            const generatedLayouts = generateLayouts(widgets, false);
            const actEpisodes = widgets.map((widget) => {
                var _a;
                return {
                    widgetId: widget.id,
                    episodes: (_a = widget.config.episodes) !== null && _a !== void 0 ? _a : [],
                };
            });
            setActiveEpisodesByWidget(actEpisodes);
            setDashboardLayout(generatedLayouts);
        }
    }, [loading, viewMode]);
    if (loading)
        return _jsx(Loader, {});
    return (_jsxs("div", { className: "fullHeight overflowYAuto relative marginLeft10 marginRight10", children: [_jsx(DashboardHeader, { projectId: projectId, dashboardId: dashboardId, activeDashboard: activeDashboard, isSupervisor: isSupervisor, onRestoreDefaultLayout: onRestoreDefaultLayout, localLayoutExists: checkLocalLayout(), viewMode: viewMode, setViewMode: setViewMode }), !loading && widgets.length === 0 ? (_jsx("div", { className: classes.fallbackScreen, children: "This dashboard has no widget" })) : (_jsx(DashboardWidgets, { allEpisodes: allEpisodes, allLayouts: dashboardLayout, widgets: widgets, stepProjects: stepProjects, dashboardLayout: dashboardLayout, progressionStatus: progressionStatus, nested: false, activeEpisodesByWidget: activeEpisodesByWidget, setActiveEpisodesByWidget: setActiveEpisodesByWidget, saveDashboardLayoutLocally: saveDashboardLayoutLocally }))] }));
};
export default Dashboard;
